import type {
  LatLngType,
  BoundingBoxType,
  Polygon,
} from "../ts-types/mapsTypes";
import { formatWithCommas, formatDistanceWithCommas } from "./formatters";
import { PERCENTAGE_THRESHOLD } from "../constants/maps-constants";

type LatLng = {
  lat: number;
  lng: number;
};

export const calculateAverageLocation = (locations: LatLng[]) => {
  let totalLat = 0;
  let totalLng = 0;
  const count = locations.length;

  locations.forEach((location) => {
    totalLat += location.lat;
    totalLng += location.lng;
  });

  return {
    lat: totalLat / count,
    lng: totalLng / count,
  };
};

export const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};

export const distanceBetweenTwoPoints = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
) => {
  const R = 20902231; // Radius of the earth in feet
  const dLat = deg2rad(lat2 - lat1);
  const dLng = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const dist = R * c; // Distance in feet

  return dist;
};

export const distanceBetweenTwoPointsVerbose = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
  withCommas: boolean = false,
): string => {
  const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceInKm = R * c;
  const distanceInMiles = distanceInKm * 0.621371;
  const distanceInFeet = distanceInMiles * 5280;
  if (distanceInMiles < 1) {
    return `${withCommas ? formatDistanceWithCommas(distanceInFeet) : distanceInFeet.toFixed(1)} ft`;
  } else {
    return `${distanceInMiles.toFixed(1)} mi`;
  }
};

export const calculatePolygonArea = (
  points: google.maps.LatLngLiteral[],
): string => {
  const latLngs = points.map(
    (point) => new google.maps.LatLng(point.lat, point.lng),
  );
  const area = google.maps.geometry.spherical.computeArea(latLngs);
  const squareMiles = area * 3.86102e-7;

  if (squareMiles >= 1) {
    return `${formatWithCommas(squareMiles)} sq mi`;
  } else {
    const squareFeet = area * 10.7639; // to sq ft
    return `${formatWithCommas(squareFeet)} sq ft`;
  }
};

export const calculateAngleBetweenPoints = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  // Convert from degrees to radians
  lat1 = (lat1 * Math.PI) / 180.0;
  lon1 = (lon1 * Math.PI) / 180.0;
  lat2 = (lat2 * Math.PI) / 180.0;
  lon2 = (lon2 * Math.PI) / 180.0;

  // Compute differences between points
  const dLon = lon2 - lon1;

  // Compute bearing (angle)
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  let brng = Math.atan2(y, x);

  // Convert from radians to degrees
  brng = (brng * 180) / Math.PI;

  // Normalize to 0 - 360 range
  brng = (brng + 360) % 360;

  return Math.round(brng) === 0 ? 0.5 : Math.round(brng);
};

/**
 * Converts a lat/lng pair to a screen point relative to the Google Map
 * instance.
 */
export const latLngToPoint = (latLng: any, map: google.maps.Map) => {
  const projection = map.getProjection();
  const bounds = map.getBounds();

  if (!projection || !bounds) {
    throw new Error("Map projection or bounds not available");
  }

  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  const zoom = map.getZoom();
  if (zoom === undefined) {
    throw new Error("Map zoom level not available");
  }
  const scale = Math.pow(2, zoom);
  const worldPoint = projection.fromLatLngToPoint(latLng);

  if (!topRight || !bottomLeft || !worldPoint) {
    throw new Error("Unable to convert coordinates to points");
  }

  return new google.maps.Point(
    (worldPoint.x - bottomLeft.x) * scale,
    (worldPoint.y - topRight.y) * scale,
  );
};

/**
 * Converts a screen point relative to the Google Map instance into a lat/lng
 * pair.
 */
export const pointToLatLng = (
  point: { x: number; y: number },
  map: google.maps.Map,
) => {
  const projection = map.getProjection();
  const bounds = map.getBounds();

  if (!projection || !bounds) {
    throw new Error("Map projection or bounds not available");
  }

  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());

  if (!topRight || !bottomLeft) {
    throw new Error("Unable to convert bounds to points");
  }

  const zoom = map.getZoom();
  if (zoom === undefined) {
    throw new Error("Map zoom level not available");
  }
  const scale = Math.pow(2, zoom);
  const worldPoint = new google.maps.Point(
    point.x / scale + bottomLeft.x,
    point.y / scale + topRight.y,
  );

  const latLng = projection.fromPointToLatLng(worldPoint);

  if (!latLng) {
    throw new Error("Unable to convert point to lat/lng");
  }

  return { lat: latLng.lat(), lng: latLng.lng() };
};

export const isInUS = (coordinates: { lat: number; lng: number }): boolean => {
  const minLat: number = 18.0; // Southern tip of Hawaii
  const maxLat: number = 71.5; // Northern tip of Alaska
  const minLng: number = -180.0; // International Date Line (east of Hawaii)
  const maxLng: number = -66.0; // Eastern tip of Maine
  return (
    coordinates.lat >= minLat &&
    coordinates.lat <= maxLat &&
    coordinates.lng >= minLng &&
    coordinates.lng <= maxLng
  );
};

export const getGeocoderResultCountry = (result?: google.maps.GeocoderResult | null): string | null => {
  const country = result?.address_components?.find((component) => component.types.includes("country"));
  return country?.short_name || null;
};

export const createBoundingBoxFromParcel = (parcelPoints: LatLngType[]) => {
  const bounds = parcelPoints.reduce(
    (acc, point) => ({
      minLat: Math.min(acc.minLat, point.lat),
      maxLat: Math.max(acc.maxLat, point.lat),
      minLng: Math.min(acc.minLng, point.lng),
      maxLng: Math.max(acc.maxLng, point.lng),
    }),
    {
      minLat: parcelPoints[0].lat,
      maxLat: parcelPoints[0].lat,
      minLng: parcelPoints[0].lng,
      maxLng: parcelPoints[0].lng,
    },
  );
  const padding = 0.00005;

  return {
    lat1: bounds.minLat - padding,
    lng1: bounds.minLng - padding,
    lat2: bounds.maxLat + padding,
    lng2: bounds.maxLng + padding,
  };
};

export const isPointInBoundingBox = (
  point: LatLng,
  boundingBox: BoundingBoxType,
): boolean => {
  const minLat = Math.min(boundingBox.lat1, boundingBox.lat2);
  const maxLat = Math.max(boundingBox.lat1, boundingBox.lat2);
  const minLng = Math.min(boundingBox.lng1, boundingBox.lng2);
  const maxLng = Math.max(boundingBox.lng1, boundingBox.lng2);

  const epsilon = 1e-10;

  return (
    point.lat + epsilon >= minLat &&
    point.lat - epsilon <= maxLat &&
    point.lng + epsilon >= minLng &&
    point.lng - epsilon <= maxLng
  );
};

const distanceToLineSegment = (
  point: LatLng,
  start: LatLng,
  end: LatLng,
): number => {
  const A = point.lng - start.lng;
  const B = point.lat - start.lat;
  const C = end.lng - start.lng;
  const D = end.lat - start.lat;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx, yy;

  if (param < 0) {
    xx = start.lng;
    yy = start.lat;
  } else if (param > 1) {
    xx = end.lng;
    yy = end.lat;
  } else {
    xx = start.lng + param * C;
    yy = start.lat + param * D;
  }

  const dx = point.lng - xx;
  const dy = point.lat - yy;

  return Math.sqrt(dx * dx + dy * dy);
};

const isPointOnLineSegment = (
  point: LatLng,
  start: LatLng,
  end: LatLng,
  threshold: number,
): boolean => {
  const d = distanceToLineSegment(point, start, end);
  return d <= threshold;
};

export const isPointInPolygon = (
  point: LatLng,
  polygon: LatLng[],
  threshold: number = 1e-5,
): boolean => {
  let inside = false;
  const x = point.lng;
  const y = point.lat;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const onSegment = isPointOnLineSegment(
      point,
      polygon[i],
      polygon[j],
      threshold,
    );
    if (onSegment) return true;

    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

export const isBuildingInParcel = (
  buildingPoints: LatLng[],
  parcelPoints: LatLng[],
  threshold: number = 1e-2,
): boolean => {
  const pointsInside = buildingPoints.reduce((count, point) => {
    return count + (isPointInPolygon(point, parcelPoints, threshold) ? 1 : 0);
  }, 0);

  const percentageInside = pointsInside / buildingPoints.length;
  return percentageInside > PERCENTAGE_THRESHOLD;
};

export function filterStructuresInParcels(
  structures: Polygon[] | null,
  parcels: LatLngType[][],
): Polygon[] {
  if (!structures || !parcels.length) return [];

  const filtered: Polygon[] = [];
  for (const structure of structures) {
    for (const parcelPoints of parcels) {
      if (isBuildingInParcel(structure.outer, parcelPoints, 1e-5)) {
        filtered.push(structure);
        break;
      }
    }
  }

  return filtered;
}

const findIntersection = (
  p1: LatLngType,
  p2: LatLngType,
  bbox: BoundingBoxType,
): LatLngType | null => {
  const minLat = Math.min(bbox.lat1, bbox.lat2);
  const maxLat = Math.max(bbox.lat1, bbox.lat2);
  const minLng = Math.min(bbox.lng1, bbox.lng2);
  const maxLng = Math.max(bbox.lng1, bbox.lng2);

  const isPointOnSegment = (point: LatLngType): boolean => {
    const segMinLat = Math.min(p1.lat, p2.lat);
    const segMaxLat = Math.max(p1.lat, p2.lat);
    const segMinLng = Math.min(p1.lng, p2.lng);
    const segMaxLng = Math.max(p1.lng, p2.lng);

    return (
      point.lat >= segMinLat &&
      point.lat <= segMaxLat &&
      point.lng >= segMinLng &&
      point.lng <= segMaxLng
    );
  };

  const isVerticalLine = Math.abs(p2.lng - p1.lng) < 0.000001;
  let m = 0;
  let b = 0;

  if (!isVerticalLine) {
    m = (p2.lat - p1.lat) / (p2.lng - p1.lng);
    b = p1.lat - m * p1.lng;
  }

  const validIntersections: LatLngType[] = [];

  if (!isVerticalLine) {
    const leftY = m * minLng + b;
    if (leftY >= minLat && leftY <= maxLat) {
      const leftPoint = { lat: leftY, lng: minLng };
      if (isPointOnSegment(leftPoint)) {
        validIntersections.push(leftPoint);
      }
    }

    // Check right edge (fixed lng = maxLng)
    const rightY = m * maxLng + b;
    if (rightY >= minLat && rightY <= maxLat) {
      const rightPoint = { lat: rightY, lng: maxLng };
      if (isPointOnSegment(rightPoint)) {
        validIntersections.push(rightPoint);
      }
    }

    // Check bottom edge (fixed lat = minLat)
    const bottomX = (minLat - b) / m;
    if (bottomX >= minLng && bottomX <= maxLng) {
      const bottomPoint = { lat: minLat, lng: bottomX };
      if (isPointOnSegment(bottomPoint)) {
        validIntersections.push(bottomPoint);
      }
    }

    // Check top edge (fixed lat = maxLat)
    const topX = (maxLat - b) / m;
    if (topX >= minLng && topX <= maxLng) {
      const topPoint = { lat: maxLat, lng: topX };
      if (isPointOnSegment(topPoint)) {
        validIntersections.push(topPoint);
      }
    }
  } else {
    // Special case: vertical line
    if (p1.lng >= minLng && p1.lng <= maxLng) {
      // Check top and bottom intersections
      const topPoint = { lat: maxLat, lng: p1.lng };
      const bottomPoint = { lat: minLat, lng: p1.lng };

      if (isPointOnSegment(topPoint)) {
        validIntersections.push(topPoint);
      }

      if (isPointOnSegment(bottomPoint)) {
        validIntersections.push(bottomPoint);
      }
    }
  }

  // Find the intersection point closest to p1
  if (validIntersections.length > 0) {
    let closestDist = Number.MAX_VALUE;
    let closestPoint: LatLngType | null = null;

    for (const point of validIntersections) {
      const dist = Math.sqrt(
        Math.pow(point.lat - p1.lat, 2) + Math.pow(point.lng - p1.lng, 2),
      );

      if (dist < closestDist) {
        closestDist = dist;
        closestPoint = point;
      }
    }

    return closestPoint;
  }

  return null;
};

const isPointInBBox = (point: LatLngType, bbox: BoundingBoxType): boolean => {
  return (
    point.lat >= Math.min(bbox.lat1, bbox.lat2) &&
    point.lat <= Math.max(bbox.lat1, bbox.lat2) &&
    point.lng >= Math.min(bbox.lng1, bbox.lng2) &&
    point.lng <= Math.max(bbox.lng1, bbox.lng2)
  );
};

export const calculateViewportAwareMidpoint = (
  point1: LatLngType,
  point2: LatLngType,
  bbox: BoundingBoxType | null,
): LatLngType => {
  // Regular midpoint
  if (!bbox || (isPointInBBox(point1, bbox) && isPointInBBox(point2, bbox))) {
    return {
      lat: (point1.lat + point2.lat) / 2,
      lng: (point1.lng + point2.lng) / 2,
    };
  }

  // Point1 inside, point2 outside
  if (isPointInBBox(point1, bbox) && !isPointInBBox(point2, bbox)) {
    const intersection = findIntersection(point1, point2, bbox);
    if (intersection) {
      return {
        lat: (point1.lat + intersection.lat) / 2,
        lng: (point1.lng + intersection.lng) / 2,
      };
    }
  }

  // Point2 inside, point1 outside
  if (!isPointInBBox(point1, bbox) && isPointInBBox(point2, bbox)) {
    const intersection = findIntersection(point2, point1, bbox);
    if (intersection) {
      return {
        lat: (point2.lat + intersection.lat) / 2,
        lng: (point2.lng + intersection.lng) / 2,
      };
    }
  }

  // Both outside, intersection midpoint
  const intersection1 = findIntersection(point1, point2, bbox);
  if (intersection1) {
    const intersection2 = findIntersection(point2, point1, bbox);
    if (intersection2) {
      return {
        lat: (intersection1.lat + intersection2.lat) / 2,
        lng: (intersection1.lng + intersection2.lng) / 2,
      };
    }
  }

  // Fallback
  return {
    lat: (point1.lat + point2.lat) / 2,
    lng: (point1.lng + point2.lng) / 2,
  };
};
